import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { tournamentApi } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { resolveLoginRedirect } from '../../services/roleContext'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Public landing page hit by the magic link in a reseller invite email.
 *
 * Two phases:
 *   1. Mount: GET /api/public/reseller-invite/:token → display reseller name
 *      and email + the password setup form.
 *   2. Submit: POST /api/public/reseller-invite/:token/accept → returns JWT,
 *      we set auth + navigate to role-based redirect.
 *
 * Backend handles all the heavy lifting (cms_users insert, membership row,
 * activate reseller_accounts, mark invite accepted, sign JWT).
 */
export default function AcceptResellerInvite() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { login: setAuth } = useAuth()

  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { t } = useLanguage()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    tournamentApi.get(`/public/reseller-invite/${token}`)
      .then(r => setInvite(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [token])

  async function handleSubmit(e) {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (!agreedTerms) { setError(t('sp_terms_must_accept', 'You must accept the Terms of Use to continue.')); return }
    setError(null)
    setSubmitting(true)
    try {
      const { data } = await tournamentApi.post(`/public/reseller-invite/${token}/accept`, {
        firstName, lastName, password, acceptedTerms: true,
      })
      setAuth(data.user, data.token)
      // Phase 1: skip MFA setup screen and just redirect by role.
      // Phase 2 will route data.requireMfaSetup === true into an MFA setup page first.
      const redirect = await resolveLoginRedirect()
      navigate(redirect, { replace: true })
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally { setSubmitting(false) }
  }

  if (loading) return <Wrapper><div style={muted}>Loading invitation…</div></Wrapper>
  if (error && !invite) {
    return (
      <Wrapper>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#dc2626' }}>Invitation problem</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
        <p style={{ color: '#64748b', fontSize: 13 }}>If you think this is a mistake, ask the person who sent the invite to send it again.</p>
      </Wrapper>
    )
  }
  if (!invite) return null

  return (
    <Wrapper>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
          INVITATION
        </div>
        <h1 style={{ margin: 0, fontSize: 24, color: 'var(--secondary-950)' }}>
          {invite.isTeamInvite
            ? `Join ${invite.resellerName}`
            : `Set up ${invite.resellerName}`}
        </h1>
        <p style={{ marginTop: 12, color: '#64748b', fontSize: 14 }}>
          {invite.isTeamInvite
            ? `You've been invited to join the ${invite.resellerName} team on InteGolf.`
            : `You've been invited to set up ${invite.resellerName} as a reseller on InteGolf.`}
        </p>
      </div>

      {error && <div style={errorBox}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <Field label="Email">
          <input type="email" value={invite.email} readOnly style={{ ...inputStyle, background: '#f8fafc', color: '#475569' }} />
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          <Field label="First name" style={{ flex: 1 }}>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} autoFocus />
          </Field>
          <Field label="Last name" style={{ flex: 1 }}>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required style={inputStyle} />
          </Field>
        </div>
        <Field label="Set a password" hint="At least 8 characters.">
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} style={inputStyle} placeholder="••••••••" />
        </Field>

        <label style={termsRow}>
          <input type="checkbox" checked={agreedTerms} onChange={e => setAgreedTerms(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            {t('sp_terms_agree_prefix', 'I have read and agree to the')}{' '}
            <a href="/terms" target="_blank" rel="noopener" style={{ color: 'var(--brand-600)' }}>{t('sp_terms_of_use', 'Terms of Use')}</a>.
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting || !agreedTerms}
          className="btn btn-dark"
          style={{ width: '100%', marginTop: 20, padding: '14px', opacity: (submitting || !agreedTerms) ? 0.6 : 1 }}
        >
          {submitting ? 'Setting up your account…' : 'Accept and continue'}
        </button>
      </form>

      <p style={{ marginTop: 24, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
        Invitation expires {new Date(invite.expiresAt).toLocaleDateString()}.
      </p>
    </Wrapper>
  )
}

function Wrapper({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)', padding: 20 }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 40 }} />
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-800)', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid var(--input-border)', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--text)', boxSizing: 'border-box' }
const termsRow = { display: 'flex', alignItems: 'flex-start', gap: 10, margin: '14px 0 4px', fontSize: 13, color: 'var(--secondary-800)', lineHeight: 1.5, cursor: 'pointer' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 14, fontSize: 13 }
const muted = { color: '#64748b', textAlign: 'center', padding: 20 }
